import { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead, { SITE_URL } from '@/components/feature/SEOHead';

const personalProducts = [
  { label: 'Home Insurance', icon: 'ri-home-4-line', desc: 'Protect your home and personal property from unexpected events.' },
  { label: 'Auto Insurance', icon: 'ri-car-line', desc: 'Coverage for your vehicles, from liability to comprehensive.' },
  { label: 'Individual Life Insurance', icon: 'ri-heart-pulse-line', desc: 'Provide financial security for your loved ones.' },
  { label: 'Renters Insurance', icon: 'ri-key-2-line', desc: "Protect your belongings even if you don't own your home." },
  { label: 'Umbrella Insurance', icon: 'ri-umbrella-line', desc: 'Extra liability protection above and beyond other policies.' },
];

const businessProducts = [
  { label: 'General Liability Insurance', icon: 'ri-shield-line', desc: 'Basic coverage protecting your business from common risks.' },
  { label: 'Professional Liability Insurance', icon: 'ri-briefcase-4-line', desc: 'Protects professionals against claims of negligence.' },
  { label: "Workers' Compensation Insurance", icon: 'ri-user-heart-line', desc: "Covers employees injured on the job." },
  { label: 'Business Auto Insurance', icon: 'ri-truck-line', desc: 'Coverage for vehicles used in your business operations.' },
  { label: 'Commercial Property Insurance', icon: 'ri-building-2-line', desc: 'Protect your business property and equipment.' },
];

const industryProducts = [
  { label: 'Farm Insurance', icon: 'ri-plant-line', desc: 'Specialized coverage for agricultural operations.' },
  { label: 'Contractor Insurance', icon: 'ri-tools-line', desc: 'Coverage tailored to contractors and tradespeople.' },
  { label: 'Restaurant Insurance', icon: 'ri-restaurant-line', desc: 'Protect your food service business from unique risks.' },
  { label: 'Manufacturer Insurance', icon: 'ri-settings-3-line', desc: 'Coverage for manufacturing operations and products.' },
  { label: 'Landscaping Services Insurance', icon: 'ri-leaf-line', desc: 'Protection for lawn care and landscaping businesses.' },
];

const insuranceJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Insurance Solutions | White Oak Insurance Group',
    description:
      'Explore personal, business, and industry insurance solutions from White Oak Insurance Group in Hattiesburg, MS. Home, auto, life, general liability, workers comp, and more.',
    url: `${SITE_URL}/insurance-solutions`,
    isPartOf: {
      '@type': 'WebSite',
      name: 'White Oak Insurance Group',
      url: SITE_URL,
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Insurance Solutions', item: `${SITE_URL}/insurance-solutions` },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Insurance Products – White Oak Insurance Group',
    description: 'A full list of personal, business, and industry insurance products offered by White Oak Insurance Group.',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home Insurance', url: `${SITE_URL}/insurance-solutions#personal` },
      { '@type': 'ListItem', position: 2, name: 'Auto Insurance', url: `${SITE_URL}/insurance-solutions#personal` },
      { '@type': 'ListItem', position: 3, name: 'Individual Life Insurance', url: `${SITE_URL}/insurance-solutions#personal` },
      { '@type': 'ListItem', position: 4, name: 'Renters Insurance', url: `${SITE_URL}/insurance-solutions#personal` },
      { '@type': 'ListItem', position: 5, name: 'Umbrella Insurance', url: `${SITE_URL}/insurance-solutions#personal` },
      { '@type': 'ListItem', position: 6, name: 'General Liability Insurance', url: `${SITE_URL}/insurance-solutions#business` },
      { '@type': 'ListItem', position: 7, name: 'Professional Liability Insurance', url: `${SITE_URL}/insurance-solutions#business` },
      { '@type': 'ListItem', position: 8, name: "Workers' Compensation Insurance", url: `${SITE_URL}/insurance-solutions#business` },
      { '@type': 'ListItem', position: 9, name: 'Business Auto Insurance', url: `${SITE_URL}/insurance-solutions#business` },
      { '@type': 'ListItem', position: 10, name: 'Commercial Property Insurance', url: `${SITE_URL}/insurance-solutions#business` },
      { '@type': 'ListItem', position: 11, name: 'Farm Insurance', url: `${SITE_URL}/insurance-solutions#industry` },
      { '@type': 'ListItem', position: 12, name: 'Contractor Insurance', url: `${SITE_URL}/insurance-solutions#industry` },
      { '@type': 'ListItem', position: 13, name: 'Restaurant Insurance', url: `${SITE_URL}/insurance-solutions#industry` },
      { '@type': 'ListItem', position: 14, name: 'Manufacturer Insurance', url: `${SITE_URL}/insurance-solutions#industry` },
      { '@type': 'ListItem', position: 15, name: 'Landscaping Services Insurance', url: `${SITE_URL}/insurance-solutions#industry` },
    ],
  },
];

export default function InsuranceSolutionsPage() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [location]);

  return (
    <div className="min-h-screen">
      <SEOHead
        title="Insurance Solutions | White Oak Insurance Group – Hattiesburg, MS"
        description="White Oak Insurance Group offers personal, business, and industry insurance in Hattiesburg, MS. Home, auto, life, general liability, workers comp, farm, contractor coverage and more."
        keywords="personal insurance Mississippi, business insurance Hattiesburg, home insurance MS, auto insurance Hattiesburg, workers compensation insurance, life insurance Mississippi"
        canonical="/insurance-solutions"
        jsonLd={insuranceJsonLd}
      />
      <Navbar />

      {/* Page Hero */}
      <section className="relative h-[500px] flex items-end justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-no-repeat bg-[#0b2240]"
          style={{
            backgroundImage:
              'url(https://storage.readdy-site.link/project_files/5a4bdf41-1aeb-4fa9-8af1-697cd10aa572/f2a9b94d-1bf0-4975-9c5a-496ed605e14b_Family.jpg?v=220b211723fceea6fa5578666a778480)',
            backgroundSize: '100% auto',
            backgroundPosition: 'center 20%',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/20 to-black/70" />
        <div className="relative z-10 w-full text-center px-6 pb-12">
          <div className="max-w-2xl mx-auto">
            <h1 className="text-5xl font-bold text-white mb-4">Insurance Solutions</h1>
            <div className="w-16 h-1 bg-[#9cbde5] mx-auto mb-5" />
            <p className="text-white/80 text-lg">
              Comprehensive personal and business insurance tailored to your needs.
            </p>
          </div>
        </div>
      </section>

      <main className="py-20">
        <div className="max-w-screen-xl mx-auto px-6 space-y-20">

          {/* Personal Insurance */}
          <div id="personal">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
              <div>
                <span className="text-[#3072b7] font-bold text-sm uppercase tracking-widest">Personal</span>
                <h2 className="text-3xl font-bold text-[#0b2240] mt-1">Personal Insurance</h2>
                <div className="w-12 h-0.5 bg-[#3072b7] mt-3" />
              </div>
              <p className="text-gray-500 text-sm max-w-md">
                Protect what you love most with our comprehensive personal insurance solutions.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {personalProducts.map((p) => (
                <div
                  key={p.label}
                  className="group bg-white border border-gray-100 hover:border-[#9cbde5] rounded-lg p-7 transition-all duration-300 hover:shadow-sm"
                >
                  <div className="w-12 h-12 flex items-center justify-center mb-4">
                    <i className={`${p.icon} text-3xl text-[#3072b7]`} />
                  </div>
                  <h3 className="text-[#0b2240] font-bold text-base mb-2">{p.label}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
                  <Link
                    to="/contact"
                    className="mt-4 inline-flex items-center gap-1 text-[#3072b7] text-sm font-semibold hover:gap-2 transition-all"
                  >
                    Get a Quote <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              ))}
            </div>
          </div>

          {/* Business Insurance */}
          <div id="business" className="bg-[#f8f9fc] rounded-xl p-10">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
              <div>
                <span className="text-[#3072b7] font-bold text-sm uppercase tracking-widest">Business</span>
                <h2 className="text-3xl font-bold text-[#0b2240] mt-1">Business Insurance</h2>
                <div className="w-12 h-0.5 bg-[#3072b7] mt-3" />
              </div>
              <p className="text-gray-500 text-sm max-w-md">
                Safeguard your business with tailored coverage solutions.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {businessProducts.map((p) => (
                <div
                  key={p.label}
                  className="group bg-white border border-gray-100 hover:border-[#9cbde5] rounded-lg p-7 transition-all duration-300 hover:shadow-sm"
                >
                  <div className="w-12 h-12 flex items-center justify-center mb-4">
                    <i className={`${p.icon} text-3xl text-[#3072b7]`} />
                  </div>
                  <h3 className="text-[#0b2240] font-bold text-base mb-2">{p.label}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
                  <Link
                    to="/contact"
                    className="mt-4 inline-flex items-center gap-1 text-[#3072b7] text-sm font-semibold hover:gap-2 transition-all"
                  >
                    Get a Quote <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              ))}
            </div>
          </div>

          {/* Insurance by Industry */}
          <div id="industry">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
              <div>
                <span className="text-[#3072b7] font-bold text-sm uppercase tracking-widest">Industry</span>
                <h2 className="text-3xl font-bold text-[#0b2240] mt-1">Insurance by Industry</h2>
                <div className="w-12 h-0.5 bg-[#3072b7] mt-3" />
              </div>
              <p className="text-gray-500 text-sm max-w-md">
                Cover the unique needs of your specific industry.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {industryProducts.map((p) => (
                <div
                  key={p.label}
                  className="group bg-white border border-gray-100 hover:border-[#9cbde5] rounded-lg p-7 transition-all duration-300 hover:shadow-sm"
                >
                  <div className="w-12 h-12 flex items-center justify-center mb-4">
                    <i className={`${p.icon} text-3xl text-[#3072b7]`} />
                  </div>
                  <h3 className="text-[#0b2240] font-bold text-base mb-2">{p.label}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
                  <Link
                    to="/contact"
                    className="mt-4 inline-flex items-center gap-1 text-[#3072b7] text-sm font-semibold hover:gap-2 transition-all"
                  >
                    Get a Quote <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Banner */}
          <div className="bg-[#0b2240] rounded-xl px-10 py-12 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">Ready to Get Started?</h3>
              <p className="text-white/70 text-sm">
                Contact our team today and let us find the right coverage for you.
              </p>
            </div>
            <Link
              to="/contact"
              className="bg-[#3072b7] hover:bg-white hover:text-[#0b2240] text-white font-bold text-sm px-10 py-4 rounded-md transition-colors whitespace-nowrap flex-shrink-0"
            >
              Request a Quote
            </Link>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}
